import React from 'react'
import { Provider } from 'react-redux'
import ReactDOM from 'react-dom/client'
import { PersistGate } from 'redux-persist/integration/react'
import { HelmetProvider } from 'react-helmet-async'

import reportWebVitals from './reportWebVitals'
import { persistor, store } from '@/stores'
import App from './App'

import '@/assets/scss/reset.scss'
import '@/assets/scss/common.css'

const CHUNK_RELOAD_GUARD_KEY = 'chunkReloadAt'
const CHUNK_RELOAD_GUARD_MS = 10_000

const isChunkLoadError = (err: any): boolean => {
  if (!err) {
    return false
  }
  if (err.name === 'ChunkLoadError') {
    return true
  }
  return /Loading (CSS )?chunk \S+ failed/i.test(String(err.message ?? ''))
}

const reloadOnChunkError = (err: any) => {
  if (!isChunkLoadError(err)) {
    return
  }
  const now = Date.now()
  const last = Number(sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) ?? 0)
  if (now - last < CHUNK_RELOAD_GUARD_MS) {
    return
  }
  sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, String(now))
  window.location.reload()
}

window.addEventListener('error', (e) => reloadOnChunkError(e.error))
window.addEventListener('unhandledrejection', (e) => reloadOnChunkError(e.reason))

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
)

async function prepare() {
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_MSW === 'true') {
    const { worker } = await import('./mocks/browser')
    await worker.start({ onUnhandledRequest: 'bypass' })
  }
}

async function bootstrap() {
  await prepare()
  root.render(
    <React.StrictMode>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <HelmetProvider>
            <App />
          </HelmetProvider>
        </PersistGate>
      </Provider>
    </React.StrictMode>
  )
  reportWebVitals()
}

bootstrap().catch((e) => {
  // eslint-disable-next-line no-console
  console.error(e)
})
